import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { restoreCheckboxLocked } from './student-lifecycle'

/**
 * TR-1100 restore dialog: bring a disabled student account back to Active. The operator
 * chooses whether the student must set a new password on next login; that choice is forced
 * (checkbox checked-and-locked) for an account that never logged in. Sessions stay dead —
 * the student logs in afresh either way. The caller owns the mutation.
 */
export function RestoreStudentDialog({
  open,
  onOpenChange,
  login,
  lastLogin,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  login: string
  lastLogin: string | null
  pending?: boolean
  error?: string | null
  onConfirm: (forcePasswordChange: boolean) => void
}) {
  const locked = restoreCheckboxLocked(lastLogin)
  const [forceChange, setForceChange] = useState(true)
  // Reset the choice to the default (checked) whenever the dialog transitions to open for a
  // new row — done during render (the documented "adjust state on prop change" pattern),
  // not in an effect, so there is no cascading-render round-trip.
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setForceChange(true)
  }

  const effective = locked ? true : forceChange

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Przywrócenie konta ucznia</DialogTitle>
          <DialogDescription>
            Konto <span className="font-mono font-medium">{login}</span> wróci do stanu aktywnego.
            Ze względów bezpieczeństwa sesje pozostają zamknięte — uczeń musi zalogować się
            ponownie.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-start gap-3">
          <Checkbox
            checked={effective}
            disabled={locked}
            onCheckedChange={(v) => setForceChange(v === true)}
            className="mt-0.5"
          />
          <span className="space-y-1">
            <Label className="font-medium">Wymuś zmianę hasła przy następnym logowaniu</Label>
            <span className="block text-sm text-muted-foreground">
              {locked
                ? 'Konto nie ukończyło pierwszego logowania — zmiana hasła zostanie wymuszona obowiązkowo.'
                : 'Zalecane. Uczeń ustawi nowe hasło zaraz po zalogowaniu.'}
            </span>
          </span>
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Anuluj
          </Button>
          <Button type="button" onClick={() => onConfirm(effective)} disabled={pending}>
            Przywróć
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
